// Simple audio utility for generating sound effects
// Also provides global user interaction tracking
//
// Important: No sounds will play until user has interacted with the page
// This ensures compliance with browser autoplay policies
//
// Usage for interaction tracking:
// import { audioEffects } from './utils/audioEffects'
// 
// // Check if user has interacted with the page
// if (audioEffects.hasUserInteracted()) {
//   // User has clicked, touched, or pressed a key
//   console.log('User has interacted!')
//   // Sounds can now be played
// }
//
// // Alternative methods:
// audioEffects.getHasInteracted() // same as hasUserInteracted()
// audioEffects.setHasInteracted(true) // manually set interaction state
//
class AudioEffects {
  // Play a short 'hint' chime for locked color hint
  playHintChime(): void {
    // "Bep-beup" error sound: two quick descending tones, triangle wave, negative feel
    this.playSoundSafely('hintChime', 200, 0.22, (audioContext) => {
      const osc1 = audioContext.createOscillator()
      const gain1 = audioContext.createGain()
      const osc2 = audioContext.createOscillator()
      const gain2 = audioContext.createGain()

      // First "bep" tone
      osc1.type = 'sine'
      osc1.frequency.setValueAtTime(480, audioContext.currentTime)
      osc1.frequency.exponentialRampToValueAtTime(320, audioContext.currentTime + 0.09)
      gain1.gain.setValueAtTime(0, audioContext.currentTime)
      gain1.gain.linearRampToValueAtTime(0.13, audioContext.currentTime + 0.01)
      gain1.gain.linearRampToValueAtTime(0.07, audioContext.currentTime + 0.05)
      gain1.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.11)

      // Second "beup" tone, starts just after first
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(340, audioContext.currentTime + 0.11)
      osc2.frequency.exponentialRampToValueAtTime(180, audioContext.currentTime + 0.20)
      gain2.gain.setValueAtTime(0, audioContext.currentTime + 0.11)
      gain2.gain.linearRampToValueAtTime(0.11, audioContext.currentTime + 0.12)
      gain2.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 0.16)
      gain2.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.22)

      osc1.connect(gain1)
      gain1.connect(audioContext.destination)
      osc2.connect(gain2)
      gain2.connect(audioContext.destination)

      osc1.start(audioContext.currentTime)
      osc1.stop(audioContext.currentTime + 0.11)
      osc2.start(audioContext.currentTime + 0.11)
      osc2.stop(audioContext.currentTime + 0.22)
    })
  }
  private audioContext: AudioContext | null = null
  private isMuted = false
  private currentSounds: Set<string> = new Set()
  private soundCooldowns: Map<string, number> = new Map()
  private isAudioContextReady = false
  private hasInteracted = false

  private async getAudioContext(): Promise<AudioContext> {
    if (!this.audioContext) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }

    // Resume AudioContext if it's suspended (browser security requirement)
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
        this.isAudioContextReady = true
      } catch (error) {
        console.debug('Could not resume AudioContext:', error)
        return this.audioContext
      }
    } else if (this.audioContext.state === 'running') {
      this.isAudioContextReady = true
    }

    return this.audioContext
  }

  // Mute/unmute all sounds
  setMuted(muted: boolean): void {
    this.isMuted = muted
    // Save preference to localStorage
    localStorage.setItem('audio-muted', muted.toString())
  }

  // Get current mute state
  isMutedState(): boolean {
    return this.isMuted
  }

  // Initialize mute state from localStorage
  initializeMuteState(): void {
    const savedMuteState = localStorage.getItem('audio-muted')
    if (savedMuteState !== null) {
      this.isMuted = savedMuteState === 'true'
    }
  }

  // Set user interaction state
  setHasInteracted(interacted: boolean): void {
    this.hasInteracted = interacted
  }

  // Get user interaction state
  getHasInteracted(): boolean {
    return this.hasInteracted
  }

  // Initialize global interaction tracking
  initializeInteractionTracking(): void {
    if (this.hasInteracted) {
      return // Already initialized and interacted
    }

    const handleInteraction = () => {
      if (!this.hasInteracted) {
        this.setHasInteracted(true)
        console.debug('First user interaction detected')
        
        // Remove listeners after first interaction
        document.removeEventListener('click', handleInteraction)
        document.removeEventListener('touchstart', handleInteraction)
        document.removeEventListener('keydown', handleInteraction)
        document.removeEventListener('mousedown', handleInteraction)
      }
    }

    // Listen for various interaction events
    document.addEventListener('click', handleInteraction, { passive: true })
    document.addEventListener('touchstart', handleInteraction, { passive: true })
    document.addEventListener('keydown', handleInteraction, { passive: true })
    document.addEventListener('mousedown', handleInteraction, { passive: true })
  }

  // Helper function to check if user has interacted (alias for easier usage)
  hasUserInteracted(): boolean {
    return this.hasInteracted
  }

  // Check if a sound can be played (prevents stacking and rapid firing)
  private canPlaySound(soundType: string, cooldownMs: number = 100): boolean {
    // Don't play any sounds before user has interacted
    if (!this.hasInteracted) {
      return false
    }

    if (this.isMuted) {
      return false
    }

    const now = Date.now()
    const lastPlayed = this.soundCooldowns.get(soundType) || 0
    
    // Check if sound is already playing or in cooldown
    if (this.currentSounds.has(soundType) || (now - lastPlayed) < cooldownMs) {
      return false
    }

    return true
  }

  // Mark a sound as playing and set cooldown
  private markSoundPlaying(soundType: string, duration: number): void {
    this.currentSounds.add(soundType)
    this.soundCooldowns.set(soundType, Date.now())
    
    // Remove from current sounds after duration
    setTimeout(() => {
      this.currentSounds.delete(soundType)
    }, duration * 1000)
  }

  // Helper method to safely play sounds with proper AudioContext handling
  private async playSoundSafely(soundType: string, cooldownMs: number, duration: number, soundFn: (audioContext: AudioContext) => void): Promise<void> {
    if (!this.canPlaySound(soundType, cooldownMs)) {
      return
    }

    try {
      this.markSoundPlaying(soundType, duration)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      soundFn(audioContext)
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Special helper for mute/unmute sounds that bypass normal mute checks
  private async playMuteSound(soundType: string, cooldownMs: number, duration: number, soundFn: (audioContext: AudioContext) => void): Promise<void> {
    // Don't play any sounds before user has interacted
    if (!this.hasInteracted) {
      return
    }

    // Only check cooldown, not mute state
    const now = Date.now()
    const lastPlayed = this.soundCooldowns.get(soundType) || 0
    
    if (this.currentSounds.has(soundType) || (now - lastPlayed) < cooldownMs) {
      return
    }

    try {
      this.markSoundPlaying(soundType, duration)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      soundFn(audioContext)
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a "pop" sound effect
  playPop(): void {
    this.playSoundSafely('pop', 50, 0.1, (audioContext) => {
      // Create oscillator for the main tone
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      // Connect nodes
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Configure the cute pop sound - higher frequency, shorter duration
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime) // Start higher
      oscillator.frequency.linearRampToValueAtTime(600, audioContext.currentTime + 0.02) // Quick rise
      oscillator.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + 0.08) // Drop to lower frequency
      
      // Configure volume envelope for cute "bloop" effect
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.005) // Very quick attack
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.02) // Hold briefly
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.08) // Quick decay
      
      // Play the sound - much shorter duration
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.08)
    })
  }

  // Generate a softer "click" sound effect
  async playClick(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('click', 100)) {
      return
    }

    try {
      this.markSoundPlaying('click', 0.1)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.type = 'triangle'
      oscillator.frequency.setValueAtTime(1200, audioContext.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.05)
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.05, audioContext.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.08)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.08)
      
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a realistic "switch click" sound effect
  async playSwitchClick(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('switchClick', 200)) {
      return
    }

    try {
      this.markSoundPlaying('switchClick', 0.15)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      // Create multiple components for a realistic switch sound
      const noise = audioContext.createBufferSource()
      const noiseFilter = audioContext.createBiquadFilter()
      const noiseGain = audioContext.createGain()
      
      const click = audioContext.createOscillator()
      const clickGain = audioContext.createGain()
      
      // Generate white noise for the mechanical "tick" part
      const bufferSize = audioContext.sampleRate * 0.1
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate)
      const output = noiseBuffer.getChannelData(0)
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1
      }
      
      noise.buffer = noiseBuffer
      
      // Filter the noise to sound more like a mechanical click
      noiseFilter.type = 'bandpass'
      noiseFilter.frequency.value = 2000
      noiseFilter.Q.value = 5
      
      // Connect noise chain
      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(audioContext.destination)
      
      // Connect click tone chain
      click.connect(clickGain)
      clickGain.connect(audioContext.destination)
      
      // Configure the click tone - sharp, brief
      click.type = 'square'
      click.frequency.setValueAtTime(1500, audioContext.currentTime)
      click.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.01)
      
      // Very sharp, brief envelopes for switch-like sound
      noiseGain.gain.setValueAtTime(0, audioContext.currentTime)
      noiseGain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.001)
      noiseGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.02)
      
      clickGain.gain.setValueAtTime(0, audioContext.currentTime)
      clickGain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.001)
      clickGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.015)
      
      // Start both components
      noise.start(audioContext.currentTime)
      click.start(audioContext.currentTime)
      
      noise.stop(audioContext.currentTime + 0.02)
      click.stop(audioContext.currentTime + 0.015)
      
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a "mute" boop sound (descending tone)
  playMuteBoop(): void {
    // Always play mute sound, even when muted (it's the last sound before muting)
    // Use special method that bypasses mute check
    this.playMuteSound('muteBoop', 200, 0.15, (audioContext) => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Descending "boop" for muting
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(500, audioContext.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.15)
      
      // Gentle envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.02)
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.08)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.15)
    })
  }

  // Generate an "unmute" boop sound (ascending tone)
  playUnmuteBoop(): void {
    // Use normal sound method since we're unmuting (should not be muted at this point)
    this.playSoundSafely('unmuteBoop', 200, 0.15, (audioContext) => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Ascending "boop" for unmuting
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(200, audioContext.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(500, audioContext.currentTime + 0.15)
      
      // Gentle envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.02)
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.08)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.15)
    })
  }

  // Generate a positive "success" chime sound for reset button
  playSuccessChime(): void {
    this.playSoundSafely('successChime', 500, 0.4, (audioContext) => {
      // Create two oscillators for a pleasant chord
      const osc1 = audioContext.createOscillator()
      const osc2 = audioContext.createOscillator()
      const gainNode1 = audioContext.createGain()
      const gainNode2 = audioContext.createGain()
      
      // Connect nodes
      osc1.connect(gainNode1)
      osc2.connect(gainNode2)
      gainNode1.connect(audioContext.destination)
      gainNode2.connect(audioContext.destination)
      
      // Pleasant major chord - C and E notes
      osc1.type = 'sine'
      osc2.type = 'sine'
      osc1.frequency.setValueAtTime(523, audioContext.currentTime) // C5
      osc2.frequency.setValueAtTime(659, audioContext.currentTime) // E5
      
      // Gentle, warm envelope
      const duration = 0.4
      
      gainNode1.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode1.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.05)
      gainNode1.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration)
      
      gainNode2.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode2.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 0.05)
      gainNode2.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration)
      
      osc1.start(audioContext.currentTime)
      osc2.start(audioContext.currentTime)
      osc1.stop(audioContext.currentTime + duration)
      osc2.stop(audioContext.currentTime + duration)
    })
  }

  // Generate a subtle "hover" sound effect for header buttons
  async playHover(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('hover', 100)) {
      return
    }

    try {
      this.markSoundPlaying('hover', 0.08)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      // Create oscillator for a softer hover sound
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      // Connect nodes
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Configure a gentler hover sound - slightly lower pitch, quicker
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(350, audioContext.currentTime) // Start lower than pop
      oscillator.frequency.linearRampToValueAtTime(480, audioContext.currentTime + 0.015) // Quicker rise
      oscillator.frequency.exponentialRampToValueAtTime(280, audioContext.currentTime + 0.06) // Drop to lower frequency
      
      // Configure volume envelope for subtle "chirp" effect
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.003) // Even quicker attack
      gainNode.gain.linearRampToValueAtTime(0.05, audioContext.currentTime + 0.015) // Hold briefly
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.06) // Quick decay
      
      // Play the sound - shorter duration than pop
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.06)
      
    } catch (error) {
      // Silently fail if audio context is not supported
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a "swoosh" sound effect for avatar flip animation
  async playSwoosh(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('swoosh', 100)) {
      return
    }

    try {
      this.markSoundPlaying('swoosh', 0.4)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      // Create noise source for the "whoosh" part
      const noise = audioContext.createBufferSource()
      const noiseFilter = audioContext.createBiquadFilter()
      const noiseGain = audioContext.createGain()
      
      // Create oscillator for the tonal "swoosh" element
      const oscillator = audioContext.createOscillator()
      const oscGain = audioContext.createGain()
      
      // Generate filtered white noise for the whoosh effect
      const bufferSize = audioContext.sampleRate * 0.4
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate)
      const output = noiseBuffer.getChannelData(0)
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1
      }
      
      noise.buffer = noiseBuffer
      
      // Filter the noise to create a smooth swoosh
      noiseFilter.type = 'bandpass'
      noiseFilter.frequency.setValueAtTime(800, audioContext.currentTime)
      noiseFilter.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.4)
      noiseFilter.Q.value = 3
      
      // Connect noise chain
      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(audioContext.destination)
      
      // Connect oscillator chain
      oscillator.connect(oscGain)
      oscGain.connect(audioContext.destination)
      
      // Configure the swoosh tone - sweeping frequency
      oscillator.type = 'triangle'
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(150, audioContext.currentTime + 0.4)
      
      // Envelope for the whoosh noise - quick attack, long tail
      noiseGain.gain.setValueAtTime(0, audioContext.currentTime)
      noiseGain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.05)
      noiseGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.4)
      
      // Envelope for the tonal element - gentler, smoother
      oscGain.gain.setValueAtTime(0, audioContext.currentTime)
      oscGain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.1)
      oscGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.35)
      
      // Start both components
      noise.start(audioContext.currentTime)
      oscillator.start(audioContext.currentTime)
      
      noise.stop(audioContext.currentTime + 0.4)
      oscillator.stop(audioContext.currentTime + 0.35)
      
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a second "swoosh" sound effect for the flip animation (slightly different)
  async playSwoosh2(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('swoosh2', 100)) {
      return
    }

    try {
      this.markSoundPlaying('swoosh2', 0.4)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      // Create noise source for the "whoosh" part
      const noise = audioContext.createBufferSource()
      const noiseFilter = audioContext.createBiquadFilter()
      const noiseGain = audioContext.createGain()
      
      // Create oscillator for the tonal "swoosh" element
      const oscillator = audioContext.createOscillator()
      const oscGain = audioContext.createGain()
      
      // Generate filtered white noise for the whoosh effect
      const bufferSize = audioContext.sampleRate * 0.4
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate)
      const output = noiseBuffer.getChannelData(0)
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1
      }
      
      noise.buffer = noiseBuffer
      
      // Filter the noise slightly differently for variation
      noiseFilter.type = 'bandpass'
      noiseFilter.frequency.setValueAtTime(600, audioContext.currentTime) // Lower start frequency
      noiseFilter.frequency.exponentialRampToValueAtTime(150, audioContext.currentTime + 0.4)
      noiseFilter.Q.value = 4 // Slightly different Q
      
      // Connect noise chain
      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(audioContext.destination)
      
      // Connect oscillator chain
      oscillator.connect(oscGain)
      oscGain.connect(audioContext.destination)
      
      // Configure the swoosh tone - slightly different sweep
      oscillator.type = 'triangle'
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime) // Lower start
      oscillator.frequency.exponentialRampToValueAtTime(120, audioContext.currentTime + 0.4)
      
      // Envelope for the whoosh noise - similar but slightly different timing
      noiseGain.gain.setValueAtTime(0, audioContext.currentTime)
      noiseGain.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.04)
      noiseGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.38)
      
      // Envelope for the tonal element - gentler, smoother
      oscGain.gain.setValueAtTime(0, audioContext.currentTime)
      oscGain.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 0.08)
      oscGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.32)
      
      // Start both components
      noise.start(audioContext.currentTime)
      oscillator.start(audioContext.currentTime)
      
      noise.stop(audioContext.currentTime + 0.38)
      oscillator.stop(audioContext.currentTime + 0.32)
      
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate a "panel open" sound effect
  playPanelOpen(): void {
    this.playSoundSafely('panelOpen', 300, 0.25, (audioContext) => {
      // Create multiple oscillators for a rich, opening sound
      const osc1 = audioContext.createOscillator()
      const osc2 = audioContext.createOscillator()
      const gainNode1 = audioContext.createGain()
      const gainNode2 = audioContext.createGain()
      
      // Connect nodes
      osc1.connect(gainNode1)
      osc2.connect(gainNode2)
      gainNode1.connect(audioContext.destination)
      gainNode2.connect(audioContext.destination)
      
      // Configure a smooth "whoosh" opening sound
      osc1.type = 'sine'
      osc2.type = 'sine'
      
      // Rising frequency sweep for "opening" feeling
      osc1.frequency.setValueAtTime(200, audioContext.currentTime)
      osc1.frequency.exponentialRampToValueAtTime(450, audioContext.currentTime + 0.2)
      
      osc2.frequency.setValueAtTime(300, audioContext.currentTime)
      osc2.frequency.exponentialRampToValueAtTime(650, audioContext.currentTime + 0.2)
      
      // Smooth envelope for gentle opening effect
      gainNode1.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode1.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 0.05)
      gainNode1.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.15)
      gainNode1.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.25)
      
      gainNode2.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode2.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.05)
      gainNode2.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.15)
      gainNode2.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.25)
      
      // Play the sound
      osc1.start(audioContext.currentTime)
      osc2.start(audioContext.currentTime)
      osc1.stop(audioContext.currentTime + 0.25)
      osc2.stop(audioContext.currentTime + 0.25)
    })
  }

  // Generate a playful "blop" sound effect for color selection
  async playBlop(): Promise<void> {
    if (!this.hasInteracted || !this.canPlaySound('blop', 150)) {
      return
    }

    try {
      this.markSoundPlaying('blop', 0.2)
      const audioContext = await this.getAudioContext()
      
      // Don't play if AudioContext couldn't be started
      if (!this.isAudioContextReady) {
        return
      }
      
      // Create oscillator for a bouncy blop sound
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      // Connect nodes
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Configure a bouncy "blop" sound
      oscillator.type = 'sine'
      
      // Quick frequency drop for "blop" effect
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1)
      oscillator.frequency.exponentialRampToValueAtTime(180, audioContext.currentTime + 0.15)
      
      // Quick attack and bouncy decay
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.06)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2)
      
      // Play the sound
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.2)
      
    } catch (error) {
      console.debug('Audio not supported:', error)
    }
  }

  // Generate arpeggio for easter egg unlocks
  playAchievementArpeggio(): void {
    this.playSoundSafely('achievementArpeggio', 800, 1.2, (audioContext) => {
      // Create a major scale arpeggio: C - E - G - C (one octave up)
      const notes = [
        523.25, // C5
        659.25, // E5  
        783.99, // G5
        1046.50 // C6
      ]
      
      const noteDelay = 0.08 // Time between notes
      const noteDuration = 0.3 // How long each note rings
      
      notes.forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        
        // Connect nodes
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        
        // Use a warm sine wave for pleasant tone
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        
        // Calculate timing for this note
        const startTime = audioContext.currentTime + (index * noteDelay)
        const endTime = startTime + noteDuration
        
        // Create a gentle envelope for each note
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(0.12, startTime + 0.02) // Quick attack
        gainNode.gain.exponentialRampToValueAtTime(0.08, startTime + 0.1) // Slight decay
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime) // Gentle release
        
        // Start and stop the oscillator
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
    })
  }
}

// Create a singleton instance
export const audioEffects = new AudioEffects()

// Initialize mute state from localStorage on module load
audioEffects.initializeMuteState()

// Initialize global interaction tracking on module load
audioEffects.initializeInteractionTracking()
